import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MdLogin, MdLogout, MdDashboard, MdAdminPanelSettings, MdHome } from 'react-icons/md';
import SpinLogo from './Logo';
import { fr } from '../i18n/fr';

function Navbar() {
  const { currentUser, logout, isOwner } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  async function handleLogout() {
    try {
      await logout();
      navigate('/');
    } catch (err) {
      console.error('Logout failed:', err);
    }
  }

  const isActive = (path) => path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <>
      {/* Mobile top bar — logo only, nav links live in the bottom bar */}
      <div className="md:hidden"
        style={{
          position: 'sticky', top: 0, zIndex: 40,
          background: 'rgba(250,248,243,0.94)',
          borderBottom: '1px solid var(--line)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
        }}>
        <div className="px-4" style={{ height: 52, display: 'flex', alignItems: 'center' }}>
          <Link to="/" className="flex items-center gap-2">
            <SpinLogo size={26} />
            <span style={{ fontFamily: 'Sora, system-ui', fontWeight: 800, fontSize: 14, color: 'var(--ink)', letterSpacing: '-0.01em' }}>
              Loyal Spin
            </span>
          </Link>
        </div>
      </div>

      {/* Desktop navbar */}
      <nav className="hidden md:block"
        style={{
          background: 'rgba(250,248,243,0.94)',
          borderBottom: '1px solid var(--line)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
        }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <Link to="/" className="flex items-center gap-2.5">
              <SpinLogo size={32} />
              <span style={{ fontFamily: 'Sora, system-ui', fontWeight: 800, fontSize: 16, color: 'var(--ink)', letterSpacing: '-0.01em' }}>
                Loyal Spin
              </span>
            </Link>

            <div className="flex items-center gap-5">
              {currentUser ? (
                <>
                  <Link to={isOwner ? '/admin' : '/dashboard'}
                    className="text-sm font-semibold transition-colors"
                    style={{ fontFamily: 'Sora, system-ui', color: 'var(--ember)' }}>
                    {isOwner ? fr.nav_dashboard_owner : fr.nav_dashboard_customer}
                  </Link>
                  <span style={{ color: 'var(--muted)', fontSize: 12 }}>{currentUser.email}</span>
                  <button onClick={handleLogout} className="btn-secondary px-4 py-2 text-sm">
                    {fr.nav_sign_out}
                  </button>
                </>
              ) : (
                <Link to="/login" className="btn-gold px-5 py-2 text-sm font-bold">
                  {fr.nav_sign_in}
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile bottom navbar */}
      <nav className="fixed bottom-0 left-0 w-full md:hidden z-50"
        style={{
          background: 'rgba(250,248,243,0.97)',
          borderTop: '1px solid var(--line)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
        }}>
        <div className="grid h-16" style={{ gridTemplateColumns: `repeat(${currentUser ? 3 : 2}, 1fr)` }}>
          <Link to="/"
            className="flex flex-col items-center justify-center gap-0.5"
            style={{ color: isActive('/') ? 'var(--ember)' : 'var(--muted)' }}>
            <MdHome className="text-2xl" />
            <span className="text-[10px] font-semibold">{fr.nav_home}</span>
          </Link>

          {currentUser && (
            <Link to={isOwner ? '/admin' : '/dashboard'}
              className="flex flex-col items-center justify-center gap-0.5"
              style={{ color: isActive(isOwner ? '/admin' : '/dashboard') ? 'var(--ember)' : 'var(--muted)' }}>
              {isOwner ? <MdAdminPanelSettings className="text-2xl" /> : <MdDashboard className="text-2xl" />}
              <span className="text-[10px] font-semibold">{isOwner ? fr.nav_admin : fr.nav_rewards}</span>
            </Link>
          )}

          {currentUser ? (
            <button
              onClick={handleLogout}
              className="flex flex-col items-center justify-center gap-0.5 w-full h-full"
              style={{ color: 'var(--muted)', background: 'none', border: 'none', cursor: 'pointer' }}>
              <MdLogout className="text-2xl" />
              <span className="text-[10px] font-semibold">{fr.nav_sign_out}</span>
            </button>
          ) : (
            <Link to="/login"
              className="flex flex-col items-center justify-center gap-0.5"
              style={{ color: isActive('/login') ? 'var(--ember)' : 'var(--muted)' }}>
              <MdLogin className="text-2xl" />
              <span className="text-[10px] font-semibold">{fr.nav_sign_in}</span>
            </Link>
          )}
        </div>
      </nav>
    </>
  );
}

export default Navbar;
